import React, { useMemo, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { StyledIcon } from "../../../components/StyledIcon/StyledIcon";
import { CustomButtonVariant } from "../../../components/CustomButton/CustomButton";
import ScheduleDetailsModal from "../../../components/ScheduleDetailsModal/ScheduleDetailsModal";
import { TableRow } from "../models/default";
import { operationModeList } from "../../RoadScheduleRequest/Section/RoadScheduleRequestStep1";
import { PipelineScheduleStatus } from "../../../modules/operational/models/GetPipelineSchedulesDTO";
import ModalScheduleUpdate from "../../RoadScheduleRequest/Section/ModalScheduleUpdate";
import { getTitleAndValue } from "../../../utils";

interface DetailsModalTableProps {
  row: TableRow;
  onAfterEdit?: () => void;
}

const DetailsModalTable: React.FC<DetailsModalTableProps> = ({
  row,
  onAfterEdit,
}) => {
  const [open, setOpen] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const operationModeLabel =
    operationModeList.find((item) => item.id === row.type)?.label || "-";

  const isEditingInvoiceReference = useMemo(() => {
    const editableStatus = [
      PipelineScheduleStatus.INVOICE_PENDING,
      PipelineScheduleStatus.IN_SCHEDULING,
      PipelineScheduleStatus.IN_LOADING,
    ];
    return editableStatus.includes(row.statusText);
  }, [row.statusText]);

  const canEdit = useMemo(() => {
    return (
      isEditingInvoiceReference &&
      row.customerInvoiceReference === "" &&
      row.attachments === undefined
    );
  }, [
    isEditingInvoiceReference,
    row.customerInvoiceReference,
    row.attachments,
  ]);

  const modalData = {
    idSchedule: getTitleAndValue("idSchedule", row.id),
    invoiceReferenceNumber: getTitleAndValue(
      "Nota Fiscal",
      row.customerInvoiceReference ?? ""
    ),
  };

  const modalHeader = [
    { label: "Razão social", value: row.customer || "-", flex: 1 },
    { label: "N. do pedido", value: row.scheduleNumber || "-" },
    { label: "Filial", value: row.subsidiary || "-" },
    {
      label: "Data do pedido",
      value:
        row.createdAt && row.createdAt !== "Data inválida"
          ? row.createdAt
          : row.scheduleDate || "-",
    },
    { label: "Solicitado por:", value: row.customerRequester || "-" },
  ];

  const modalHighlightCards = [
    {
      items: [
        { label: "Transportadora", value: row.shippingCompany || "-" },
        { label: "Motorista", value: row.driver || "-" },
      ],
    },
    {
      items: [
        { label: "Placa do cavalo", value: row.vehicle_plate || "-" },
        { label: "Placa da carreta", value: row.horse_plate || "-" },
      ],
    },
  ];

  const invoiceFields =
    row.type === "SHIPMENT_ON_ACCOUNT_AND_ORDER" && row.customerInvoiceReference
      ? [
          {
            label: "Número da Nota Fiscal",
            value: row.customerInvoiceReference,
          },
          {
            label: "Nota Fiscal (anexo)",
            value: row.attachments?.[0]?.fileName ?? "Sem anexo",
          },
        ]
      : [];

  const modalDetails = [
    { label: "Produto", value: row.product || "-" },
    { label: "Modalidade", value: row.mod || "-" },
    { label: "Quantidade", value: `${row.quantity} ${row.unit}` },
    { label: "Condição de pagamento", value: row.paymentCondition || "-" },
    { label: "Modo de Operação", value: operationModeLabel },
    ...invoiceFields,
  ];

  const modalFooter = {
    label: "Observação",
    value: row.customerRequesterNote || "-",
  };

  const actions = useMemo(() => {
    const items = [];

    if (isEditingInvoiceReference) {
      items.push({
        label: "Editar",
        variant: canEdit
          ? CustomButtonVariant.CONTAINED
          : CustomButtonVariant.DISABLED,
        onClick: () => {
          handleClose();
          setIsOpenUpdate(true);
        },
      });
    }

    items.push({
      label: "Fechar",
      variant: CustomButtonVariant.CONTAINED,
      onClick: handleClose,
    });

    return items;
  }, [isEditingInvoiceReference, canEdit]);

  return (
    <>
      <Tooltip title="Ver detalhes">
        <IconButton onClick={handleOpen}>
          <StyledIcon iconType="info" />
        </IconButton>
      </Tooltip>

      <ScheduleDetailsModal
        open={open}
        onClose={handleClose}
        title="Detalhes do pedido de agendamento"
        header={modalHeader}
        highlightCards={modalHighlightCards}
        details={modalDetails}
        footer={modalFooter}
        actions={actions}
      />

      <ModalScheduleUpdate
        handleClose={(openState) => setIsOpenUpdate(openState)}
        cleanFilter={onAfterEdit}
        isOpen={isOpenUpdate}
        data={modalData}
      />
    </>
  );
};

export default DetailsModalTable;
